import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DownloadIcon, ShieldIcon, ZapIcon, ClockIcon } from 'lucide-react';
import { PaymentIcons } from './PaymentIcons';
export function ClosingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="w-full py-12 sm:py-16 px-4 sm:px-6" style={{
    background: 'linear-gradient(180deg, #0A1628 0%, #0D1F3C 50%, #0A1628 100%)'
  }} data-id="element-1">
      <div className="max-w-3xl mx-auto text-center" data-id="element-2">
        {/* Urgency icon */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={isInView ? {
        opacity: 1,
        scale: 1
      } : {}} transition={{
        duration: 0.6
      }} className="flex justify-center mb-5" data-id="element-3">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{
          background: 'linear-gradient(135deg, rgba(201,168,76,0.2), rgba(240,208,128,0.1))',
          border: '2px solid #C9A84C'
        }} data-id="element-4">
            <ZapIcon size={26} className="text-gold-warm" data-id="element-5" />
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-cinzel font-black text-2xl sm:text-3xl md:text-5xl mb-5 leading-tight" style={{
        color: '#FFF8E7'
      }} data-id="element-6">
          No dejes la protección de tu familia{' '}
          <span className="text-gold-light" data-id="element-7">para mañana.</span>
        </motion.h2>

        <div className="divider-gold mx-auto mb-6" style={{
        width: '140px'
      }} data-id="element-8" />

        {/* Body text */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.25
      }} className="font-lato text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto" style={{
        color: 'rgba(255,248,231,0.85)'
      }} data-id="element-9">
          El mal no descansa, pero tu fe es más poderosa cuando sabes cómo
          usarla. Descarga el manual ahora mismo en tu celular y empieza a ver
          los milagros hoy.
        </motion.p>

        {/* Instant access badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.4
      }} className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-10">
          {[{
          Icon: ZapIcon,
          label: 'Acceso Inmediato'
        }, {
          Icon: ClockIcon,
          label: 'Disponible 24/7'
        }, {
          Icon: ShieldIcon,
          label: 'Garantía 7 Días'
        }].map(({
          Icon,
          label
        }) => <div key={label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{
          background: 'rgba(201,168,76,0.1)',
          border: '1px solid rgba(201,168,76,0.3)'
        }} data-id="element-11">
              <Icon size={13} className="text-gold-warm" data-id="element-12" />
              <span className="font-lato text-xs text-gold-warm font-bold" data-id="element-13">
                {label}
              </span>
            </div>)}
        </motion.div>

        {/* Final CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.55
      }} data-id="element-14">
          <button className="cta-button pulse-glow inline-flex items-center gap-2 px-5 py-4 sm:py-5 rounded-xl text-navy-deep font-cinzel font-black text-base sm:text-xl uppercase tracking-wide w-full justify-center mb-3" style={{
          background: 'linear-gradient(135deg, #C9A84C 0%, #F0D080 50%, #C9A84C 100%)'
        }} aria-label="Descargar Escudo de Fe ahora mismo" data-id="element-15">
            <DownloadIcon size={22} className="flex-shrink-0" data-id="element-16" />
            <span data-id="element-17">DESCARGAR "ESCUDO DE FE" AHORA</span>
          </button>

          <p className="font-lato text-xs mb-1" style={{
          color: 'rgba(255,248,231,0.5)'
        }} data-id="element-18">
            Acceso inmediato · Correo electrónico y WhatsApp
          </p>

          <PaymentIcons data-id="element-19" />
        </motion.div>

        {/* Footer note */}
        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.7,
        delay: 0.8
      }} className="mt-12 pt-6" style={{
        borderTop: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-20">
          <div className="flex justify-center mb-3" data-id="element-21">
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-22">
              <path d="M16 2L20 12H30L22 18L25 28L16 22L7 28L10 18L2 12H12L16 2Z" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-23" />
              <path d="M16 8L18.5 14H25L20 17.5L22 24L16 20L10 24L12 17.5L7 14H13.5L16 8Z" fill="rgba(201,168,76,0.2)" data-id="element-24" />
            </svg>
          </div>
          <p className="font-cinzel text-xs tracking-widest uppercase" style={{
          color: 'rgba(201,168,76,0.5)'
        }} data-id="element-25">
            Escudo de Fe — Protección Espiritual para tu Familia
          </p>
          <p className="font-lato text-xs mt-1" style={{
          color: 'rgba(255,248,231,0.25)'
        }} data-id="element-26">
            © 2025 Escudo de Fe. Todos los derechos reservados.
          </p>
        </motion.div>
      </div>
    </section>;
}